// S8 — Validación de corridas de tienda (supervisor).
//
// Paridad con la validación de líneas (S2): el supervisor ve las corridas de
// las plantas de su scope en una fecha operativa y las valida una por una, o
// las invalida con razón ≥20. Las pendientes salen primero. La edición del
// líder devuelve la corrida a pendiente (igual que líneas).

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { RegistroTienda } from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import {
  useCorridasTiendaSupervisor,
  useInvalidarCorridaTienda,
  useValidarCorridaTienda,
} from '../../lib/api/queries';

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

const hora = (iso: string) => iso.slice(11, 16);

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function S8TiendasPage() {
  const { state } = useAuth();
  const [fecha, setFecha] = useState(hoyISO());
  const [aInvalidar, setAInvalidar] = useState<RegistroTienda | null>(null);
  const { data, isLoading, isError, error, isFetching } = useCorridasTiendaSupervisor(fecha);
  const validar = useValidarCorridaTienda();

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const corridas = data ?? [];
  const pendientes = corridas.filter((c) => !c.validado_at);
  const validadas = corridas.filter((c) => c.validado_at);

  const onValidar = async (id: string) => {
    try {
      await validar.mutateAsync(id);
      toast.success('Corrida validada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos validar la corrida');
    }
  };

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Tiendas — validación</h1>
            <p className="text-sm text-fg-tertiary">
              Corridas de reempaque de sus plantas. Igual que las líneas: lo que usted valida es lo
              que factura.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="s8-fecha">Fecha operativa</Label>
              <Input
                id="s8-fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-40"
              />
            </div>
            {isFetching ? <Spinner className="mb-2 size-4" /> : null}
          </div>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-tertiary">
            <Spinner className="size-5" /> Cargando corridas…
          </div>
        ) : isError ? (
          <p className="py-8 text-center text-sm text-crit">
            {error instanceof Error ? error.message : 'No pudimos cargar las corridas'}
          </p>
        ) : corridas.length === 0 ? (
          <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
            Sin corridas de tienda en esta fecha.
          </div>
        ) : (
          <>
            <p className="text-sm text-fg-secondary">
              <span className={cn('font-medium', pendientes.length > 0 ? 'text-warn' : 'text-fg')}>
                {pendientes.length} pendiente{pendientes.length === 1 ? '' : 's'}
              </span>{' '}
              · {validadas.length} validada{validadas.length === 1 ? '' : 's'}
            </p>
            {/* Cards apiladas, MISMO patrón que la validación de líneas en S2
                (BloqueRow): columna en mobile, fila en desktop. El supervisor
                valida desde el teléfono caminando la planta. */}
            <div className="flex flex-col gap-2">
              {corridas.map((c) => (
                <CorridaCard
                  key={c.id}
                  corrida={c}
                  isValidating={validar.isPending}
                  onValidar={() => void onValidar(c.id)}
                  onInvalidar={() => setAInvalidar(c)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {aInvalidar ? (
        <InvalidarDialog corrida={aInvalidar} onClose={() => setAInvalidar(null)} />
      ) : null}
    </RoleLayout>
  );
}

/**
 * Insumo como texto: capturado + ratio REAL por unidad (capturado ÷ unidades —
 * "cuántos le agregan a cada unidad"). NO se compara contra la config de
 * tienda_skus: no es un "esperado" confiable (decisión del usuario 2026-07-03).
 */
function insumoTexto(label: string, capturado: number | null, unidades: number): string | null {
  if (!capturado) return null;
  const ratio = unidades > 0 ? Math.round((capturado / unidades) * 10) / 10 : null;
  return `${label} ${fmt(capturado)}${ratio !== null ? ` (${ratio}/und)` : ''}`;
}

/**
 * Card de corrida — MISMO patrón que BloqueRow de S2 (validación de líneas):
 * Card apilada, columna en mobile / fila en desktop, verde suave si validada,
 * botones Validar/Invalidar a la derecha. El supervisor valida desde el
 * teléfono igual que valida los bloques de las líneas.
 */
function CorridaCard({
  corrida: c,
  isValidating,
  onValidar,
  onInvalidar,
}: {
  corrida: RegistroTienda;
  isValidating: boolean;
  onValidar: () => void;
  onInvalidar: () => void;
}) {
  const validada = c.validado_at !== null;
  const insumos = [
    insumoTexto('Stickers', c.cantidad_stickers, c.cantidad_unidades),
    insumoTexto('Termos', c.cantidad_termos, c.cantidad_unidades),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card className={validada ? 'border-ok bg-ok-soft/30' : 'border-border'}>
      <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold">
              {hora(c.hora_inicio)}–{hora(c.hora_fin)}
            </span>
            <span className="text-sm text-fg">{c.tienda_nombre ?? '—'}</span>
            {validada ? (
              <span className="rounded bg-ok-soft px-1.5 py-0.5 text-[10px] font-semibold text-ok">
                ✓ Validada
              </span>
            ) : (
              <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[10px] font-semibold text-warn">
                Pendiente
              </span>
            )}
          </div>
          <p className="text-sm">
            <strong>{fmt(c.cantidad_unidades)}</strong> u ·{' '}
            <span className="tabular-nums text-fg-secondary">{c.sku_codigo_cliente ?? ''}</span>{' '}
            {c.sku_descripcion ?? ''}
          </p>
          {insumos ? <p className="text-xs text-fg-secondary">{insumos}</p> : null}
          <p className="text-xs text-fg-tertiary">
            {c.lider_nombre ? `Líder: ${c.lider_nombre} ${c.lider_apellido ?? ''}` : 'Sin líder'}
            {c.orden_produccion ? ` · Orden: ${c.orden_produccion}` : ''}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!validada ? (
            <Button size="sm" onClick={onValidar} disabled={isValidating}>
              {isValidating ? '...' : 'Validar'}
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={onInvalidar}>
            Invalidar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Modal de invalidación con razón obligatoria ≥20 (mismo criterio que S2). */
function InvalidarDialog({ corrida, onClose }: { corrida: RegistroTienda; onClose: () => void }) {
  const invalidar = useInvalidarCorridaTienda();
  const [razon, setRazon] = useState('');

  const confirmar = async () => {
    try {
      await invalidar.mutateAsync({ id: corrida.id, razon: { razon_invalidacion: razon.trim() } });
      toast.success('Corrida invalidada');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos invalidar la corrida');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invalidar corrida</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg-secondary">
            {corrida.tienda_nombre ?? 'Destino'} · {fmt(corrida.cantidad_unidades)} unidades ·{' '}
            {hora(corrida.hora_inicio)}–{hora(corrida.hora_fin)}. La corrida queda invalidada
            (soft-delete) y deja de contar en productividad y facturación.
          </p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="s8-razon">Razón (mínimo 20 caracteres) *</Label>
            <Input
              id="s8-razon"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej.: unidades duplicadas con la corrida de las 10:00"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={razon.trim().length < 20 || invalidar.isPending}
              onClick={() => void confirmar()}
            >
              Invalidar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
